import type { ReferenceTag } from '../types';

/**
 * Kling O3's reference mode (through Venice) takes two kinds of picture: elements, the
 * characters and objects to keep looking the same (named @Element1–4 in the prompt), and
 * scene pictures that set the place, light and style (@Image1–4). A picture's Reference tag
 * decides which it is: backgrounds and styles set the scene, everything else is an element.
 * https://docs.venice.ai/guides/media/reference-to-video
 */
export const KLING_MAX_ELEMENTS = 4;
export const KLING_MAX_SCENES = 4;

export type KlingRole = 'element' | 'scene';

export const klingRole = (tag?: ReferenceTag): KlingRole => (tag === 'background' || tag === 'style' ? 'scene' : 'element');

export interface KlingSlot {
    role: KlingRole;
    /** How the prompt names it: @Element1… or @Image1…, numbered per role in order. */
    tag: string;
}

export const klingSlots = (tags: (ReferenceTag | undefined)[]): KlingSlot[] => {
    let elements = 0;
    let scenes = 0;
    return tags.map((tag): KlingSlot => (klingRole(tag) === 'scene'
        ? { role: 'scene', tag: `@Image${++scenes}` }
        : { role: 'element', tag: `@Element${++elements}` }));
};

/** Problems with the pictures for one request (empty when they can be sent). */
export const checkKlingReferences = (slots: KlingSlot[]): string[] => {
    const elements = slots.filter(slot => slot.role === 'element').length;
    const scenes = slots.length - elements;
    const problems: string[] = [];
    if (elements > KLING_MAX_ELEMENTS) {
        problems.push(`Kling takes at most ${KLING_MAX_ELEMENTS} characters or objects, and ${elements} are attached. Tag a picture Background or Style to use it as a scene, or remove one.`);
    }
    if (scenes > KLING_MAX_SCENES) {
        problems.push(`Kling takes at most ${KLING_MAX_SCENES} scene pictures (Background or Style), and ${scenes} are attached.`);
    }
    return problems;
};

export interface KlingElement {
    frontalImageUrl: string;
}

/** The pictures (in the same order as the slots) as the request's elements and scene pictures. */
export const klingReferenceFields = (slots: KlingSlot[], pictures: string[]): { elements: KlingElement[]; sceneImageUrls: string[] } => ({
    elements: slots.flatMap((slot, i) => (slot.role === 'element' && pictures[i] ? [{ frontalImageUrl: pictures[i] }] : [])),
    sceneImageUrls: slots.flatMap((slot, i) => (slot.role === 'scene' && pictures[i] ? [pictures[i]] : [])),
});
